import { c as _c } from "react/compiler-runtime";
import { alpha, Box, Button, Container, Stack, Typography, useTheme } from "@mui/material";
import Link from "next/link";
import React, { useRef } from "react";
import { type OrbitControls as ThreeOrbitControls } from "three-stdlib";
import { useHeroOrbitModelMotion } from "#/features/homePage/hooks/useHeroOrbitModelMotion";
import { useLandingDecor3dMobileEntrance } from "#/features/homePage/hooks/useLandingDecor3dMobileEntrance";
import { LANDING_DECOR_BRAND_BASE_AZIMUTH, LANDING_DECOR_BRAND_BASE_POLAR, LANDING_DECOR_CANVAS_SIZE_PX, LANDING_DECOR_GLASS_OPACITY_SX, LANDING_DECOR_MODEL_CAMERA, LANDING_DECOR_MODEL_DISTANCE, LANDING_DECOR_MODEL_FOV, LANDING_DECOR_MODEL_TARGET } from "#/features/homePage/lib/landingDecor3dConstants";
import { LANDING_PRIMARY_PLAYGROUND_HREF } from "#/features/homePage/lib/landingPlaygroundDemos";
import { HomeLandingHeroPreview } from "#/features/homePage/ui/landing/HomeLandingHeroPreview";
import type { TranslationFunctions } from "#/i18n/i18n-types";
import { LogoModelView } from "#/shared/ui/molecules/LogoModelView";
type HomeLandingHeroProps = {
  LL: TranslationFunctions;
};
const AmbientBackground = () => {
  const $ = _c(8);
  const theme = useTheme();
  let t0;
  if ($[0] !== theme.appDesign.accent) {
    t0 = alpha(theme.appDesign.accent, 0.16);
    $[0] = theme.appDesign.accent;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== theme.appDesign.accentSoft) {
    t1 = alpha(theme.appDesign.accentSoft, 0.12);
    $[2] = theme.appDesign.accentSoft;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== theme.appDesign.surfaceLow) {
    t2 = alpha(theme.appDesign.surfaceLow, 0.12);
    $[4] = theme.appDesign.surfaceLow;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = `
          radial-gradient(circle at 18% 18%, ${t0} 0%, transparent 42%),
          radial-gradient(circle at 82% 12%, ${t1} 0%, transparent 36%),
          linear-gradient(180deg, ${t2} 0%, transparent 55%)
        `;
  let t4;
  if ($[6] !== t3) {
    t4 = <Box sx={{
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100vh",
      background: t3,
      pointerEvents: "none"
    }} />;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
};
export const HomeLandingHero: React.FC<HomeLandingHeroProps> = t0 => {
  const $ = _c(110);
  const {
    LL
  } = t0;
  const theme = useTheme();
  const brandControlsRef = useRef(null);
  const {
    mobileEntranceSx
  } = useLandingDecor3dMobileEntrance("hero");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      controlsRef: brandControlsRef,
      baseAzimuth: LANDING_DECOR_BRAND_BASE_AZIMUTH,
      basePolar: LANDING_DECOR_BRAND_BASE_POLAR,
      invertPointerX: false
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  useHeroOrbitModelMotion(t1);
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      position: "relative",
      overflow: "visible",
      bgcolor: "background.default",
      pt: {
        xs: 14,
        md: 18
      },
      pb: {
        xs: 8,
        md: 10
      }
    };
    t3 = <AmbientBackground />;
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      position: "absolute",
      top: {
        xs: -88,
        sm: -170,
        md: -280
      },
      right: {
        xs: -118,
        sm: -180,
        md: -320
      },
      width: LANDING_DECOR_CANVAS_SIZE_PX,
      height: LANDING_DECOR_CANVAS_SIZE_PX,
      opacity: LANDING_DECOR_GLASS_OPACITY_SX,
      filter: "blur(0.4px)",
      pointerEvents: "none",
      maskImage: "radial-gradient(circle at 48% 50%, rgba(0,0,0,0.98) 0%, rgba(0,0,0,0.9) 32%, rgba(0,0,0,0.46) 66%, transparent 90%)"
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== mobileEntranceSx) {
    t5 = [t4, mobileEntranceSx];
    $[4] = mobileEntranceSx;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <LogoModelView controlsRef={brandControlsRef} interactive={false} cameraPosition={LANDING_DECOR_MODEL_CAMERA} cameraFov={LANDING_DECOR_MODEL_FOV} target={LANDING_DECOR_MODEL_TARGET} distanceRange={LANDING_DECOR_MODEL_DISTANCE} />;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== t5) {
    t7 = <Box sx={t5}>{t6}</Box>;
    $[7] = t5;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      px: {
        xs: 2,
        sm: 3,
        md: 4
      }
    };
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      display: "grid",
      gridTemplateColumns: {
        xs: "1fr",
        lg: "minmax(0, 5fr) minmax(0, 7fr)"
      },
      gap: {
        xs: 4,
        md: 6
      },
      alignItems: "center"
    };
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      minWidth: 0
    };
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      position: "relative",
      zIndex: 1
    };
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      maxWidth: 700,
      fontSize: {
        xs: "2.15rem",
        sm: "3.4rem",
        md: "4.8rem"
      },
      lineHeight: {
        xs: 1.08,
        md: 1.02
      }
    };
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  let t13;
  if ($[14] !== LL) {
    t13 = LL.HOME_LANDING_TITLE();
    $[14] = LL;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  let t14;
  if ($[16] !== t13) {
    t14 = <Typography variant="h1" sx={t12}>{t13}</Typography>;
    $[16] = t13;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  let t15;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      maxWidth: 620,
      fontSize: {
        xs: "1rem",
        md: "1.08rem"
      },
      lineHeight: 1.75
    };
    $[18] = t15;
  } else {
    t15 = $[18];
  }
  let t16;
  if ($[19] !== LL) {
    t16 = LL.HOME_LANDING_SUBTITLE();
    $[19] = LL;
    $[20] = t16;
  } else {
    t16 = $[20];
  }
  let t17;
  if ($[21] !== t16) {
    t17 = <Typography variant="body1" color="text.secondary" sx={t15}>{t16}</Typography>;
    $[21] = t16;
    $[22] = t17;
  } else {
    t17 = $[22];
  }
  let t18;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      xs: "column",
      sm: "row"
    };
    $[23] = t18;
  } else {
    t18 = $[23];
  }
  let t19;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = {
      pt: 1
    };
    $[24] = t19;
  } else {
    t19 = $[24];
  }
  let t20;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = {
      width: {
        xs: "100%",
        sm: "auto"
      }
    };
    $[25] = t20;
  } else {
    t20 = $[25];
  }
  let t21;
  if ($[26] !== LL) {
    t21 = LL.TRY_IT_OUT_NOW();
    $[26] = LL;
    $[27] = t21;
  } else {
    t21 = $[27];
  }
  let t22;
  if ($[28] !== t21) {
    t22 = <Button component={Link} data-testid="cta-to-playground" href={LANDING_PRIMARY_PLAYGROUND_HREF} variant="contained" size="large" sx={t20}>{t21}</Button>;
    $[28] = t21;
    $[29] = t22;
  } else {
    t22 = $[29];
  }
  let t23;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = {
      width: {
        xs: "100%",
        sm: "auto"
      }
    };
    $[30] = t23;
  } else {
    t23 = $[30];
  }
  let t24;
  if ($[31] !== LL) {
    t24 = LL.BROWSE_PROJECTS();
    $[31] = LL;
    $[32] = t24;
  } else {
    t24 = $[32];
  }
  let t25;
  if ($[33] !== t24) {
    t25 = <Button component={Link} href="/playground?view=browse" variant="outlined" size="large" sx={t23}>{t24}</Button>;
    $[33] = t24;
    $[34] = t25;
  } else {
    t25 = $[34];
  }
  let t26;
  if ($[35] !== t22 || $[36] !== t25) {
    t26 = <Stack direction={t18} spacing={1.5} sx={t19}>{t22}{t25}</Stack>;
    $[35] = t22;
    $[36] = t25;
    $[37] = t26;
  } else {
    t26 = $[37];
  }
  let t27;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = {
      xs: "column",
      sm: "row"
    };
    $[38] = t27;
  } else {
    t27 = $[38];
  }
  let t28;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = {
      xs: "flex-start",
      sm: "center"
    };
    $[39] = t28;
  } else {
    t28 = $[39];
  }
  let t29;
  if ($[40] !== LL) {
    t29 = LL.HOME_HERO_FAQ_LINK();
    $[40] = LL;
    $[41] = t29;
  } else {
    t29 = $[41];
  }
  let t30;
  if ($[42] !== LL) {
    t30 = LL.HOME_HERO_FAQ_LINK_SUFFIX();
    $[42] = LL;
    $[43] = t30;
  } else {
    t30 = $[43];
  }
  let t31;
  if ($[44] !== t29 || $[45] !== t30) {
    t31 = <Stack direction={t27} spacing={1} alignItems={t28}><Button component={Link} href="#faq" variant="text" color="inherit">{t29}{t30}</Button></Stack>;
    $[44] = t29;
    $[45] = t30;
    $[46] = t31;
  } else {
    t31 = $[46];
  }
  let t32;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = {
      pt: 1
    };
    $[47] = t32;
  } else {
    t32 = $[47];
  }
  let t33;
  if ($[48] !== theme.appDesign.surfaceHigh) {
    t33 = alpha(theme.appDesign.surfaceHigh, 0.82);
    $[48] = theme.appDesign.surfaceHigh;
    $[49] = t33;
  } else {
    t33 = $[49];
  }
  let t34;
  if ($[50] !== theme.appDesign.outline) {
    t34 = alpha(theme.appDesign.outline, 0.14);
    $[50] = theme.appDesign.outline;
    $[51] = t34;
  } else {
    t34 = $[51];
  }
  const t35 = `1px solid ${t34}`;
  let t36;
  if ($[52] !== t33 || $[53] !== t35) {
    t36 = {
      px: 1.25,
      py: 0.75,
      borderRadius: 99,
      bgcolor: t33,
      border: t35
    };
    $[52] = t33;
    $[53] = t35;
    $[54] = t36;
  } else {
    t36 = $[54];
  }
  let t37;
  if ($[55] !== LL) {
    t37 = LL.HOME_LANG_JS_TITLE();
    $[55] = LL;
    $[56] = t37;
  } else {
    t37 = $[56];
  }
  let t38;
  if ($[57] !== t37) {
    t38 = <Typography variant="caption" color="text.secondary">{t37}</Typography>;
    $[57] = t37;
    $[58] = t38;
  } else {
    t38 = $[58];
  }
  let t39;
  if ($[59] !== t36 || $[60] !== t38) {
    t39 = <Box sx={t36}>{t38}</Box>;
    $[59] = t36;
    $[60] = t38;
    $[61] = t39;
  } else {
    t39 = $[61];
  }
  let t40;
  if ($[62] !== theme.appDesign.surfaceHigh) {
    t40 = alpha(theme.appDesign.surfaceHigh, 0.82);
    $[62] = theme.appDesign.surfaceHigh;
    $[63] = t40;
  } else {
    t40 = $[63];
  }
  let t41;
  if ($[64] !== theme.appDesign.outline) {
    t41 = alpha(theme.appDesign.outline, 0.14);
    $[64] = theme.appDesign.outline;
    $[65] = t41;
  } else {
    t41 = $[65];
  }
  const t42 = `1px solid ${t41}`;
  let t43;
  if ($[66] !== t40 || $[67] !== t42) {
    t43 = {
      px: 1.25,
      py: 0.75,
      borderRadius: 99,
      bgcolor: t40,
      border: t42
    };
    $[66] = t40;
    $[67] = t42;
    $[68] = t43;
  } else {
    t43 = $[68];
  }
  let t44;
  if ($[69] !== LL) {
    t44 = LL.HOME_LANG_PYTHON_TITLE();
    $[69] = LL;
    $[70] = t44;
  } else {
    t44 = $[70];
  }
  let t45;
  if ($[71] !== t44) {
    t45 = <Typography variant="caption" color="text.secondary">{t44}</Typography>;
    $[71] = t44;
    $[72] = t45;
  } else {
    t45 = $[72];
  }
  let t46;
  if ($[73] !== t43 || $[74] !== t45) {
    t46 = <Box sx={t43}>{t45}</Box>;
    $[73] = t43;
    $[74] = t45;
    $[75] = t46;
  } else {
    t46 = $[75];
  }
  let t47;
  if ($[76] !== theme.appDesign.surfaceHigh) {
    t47 = alpha(theme.appDesign.surfaceHigh, 0.82);
    $[76] = theme.appDesign.surfaceHigh;
    $[77] = t47;
  } else {
    t47 = $[77];
  }
  let t48;
  if ($[78] !== theme.appDesign.outline) {
    t48 = alpha(theme.appDesign.outline, 0.14);
    $[78] = theme.appDesign.outline;
    $[79] = t48;
  } else {
    t48 = $[79];
  }
  const t49 = `1px solid ${t48}`;
  let t50;
  if ($[80] !== t47 || $[81] !== t49) {
    t50 = {
      px: 1.25,
      py: 0.75,
      borderRadius: 99,
      bgcolor: t47,
      border: t49
    };
    $[80] = t47;
    $[81] = t49;
    $[82] = t50;
  } else {
    t50 = $[82];
  }
  let t51;
  if ($[83] !== LL) {
    t51 = LL.HOME_PILLAR_REPLAY_TITLE();
    $[83] = LL;
    $[84] = t51;
  } else {
    t51 = $[84];
  }
  let t52;
  if ($[85] !== t51) {
    t52 = <Typography variant="caption" color="text.secondary">{t51}</Typography>;
    $[85] = t51;
    $[86] = t52;
  } else {
    t52 = $[86];
  }
  let t53;
  if ($[87] !== t50 || $[88] !== t52) {
    t53 = <Box sx={t50}>{t52}</Box>;
    $[87] = t50;
    $[88] = t52;
    $[89] = t53;
  } else {
    t53 = $[89];
  }
  let t54;
  if ($[90] !== t39 || $[91] !== t46 || $[92] !== t53) {
    t54 = <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap={true} sx={t32}>{t39}{t46}{t53}</Stack>;
    $[90] = t39;
    $[91] = t46;
    $[92] = t53;
    $[93] = t54;
  } else {
    t54 = $[93];
  }
  let t55;
  if ($[94] !== t14 || $[95] !== t17 || $[96] !== t26 || $[97] !== t31 || $[98] !== t54) {
    t55 = <Box sx={t10}><Stack spacing={2.5} sx={t11}>{t14}{t17}{t26}{t31}{t54}</Stack></Box>;
    $[94] = t14;
    $[95] = t17;
    $[96] = t26;
    $[97] = t31;
    $[98] = t54;
    $[99] = t55;
  } else {
    t55 = $[99];
  }
  let t56;
  if ($[100] === Symbol.for("react.memo_cache_sentinel")) {
    t56 = {
      minWidth: 0
    };
    $[100] = t56;
  } else {
    t56 = $[100];
  }
  let t57;
  if ($[101] === Symbol.for("react.memo_cache_sentinel")) {
    t57 = {
      position: "relative",
      zIndex: 1,
      minWidth: 0,
      mx: {
        xs: "auto",
        lg: 0
      }
    };
    $[101] = t57;
  } else {
    t57 = $[101];
  }
  let t58;
  if ($[102] !== LL) {
    t58 = <Box sx={t56}><Box sx={t57}><HomeLandingHeroPreview LL={LL} /></Box></Box>;
    $[102] = LL;
    $[103] = t58;
  } else {
    t58 = $[103];
  }
  let t59;
  if ($[104] !== t55 || $[105] !== t58) {
    t59 = <Container maxWidth="xl" sx={t8}><Box sx={t9}>{t55}{t58}</Box></Container>;
    $[104] = t55;
    $[105] = t58;
    $[106] = t59;
  } else {
    t59 = $[106];
  }
  let t60;
  if ($[107] !== t59 || $[108] !== t7) {
    t60 = <Box component="section" sx={t2}>{t3}{t7}{t59}</Box>;
    $[107] = t59;
    $[108] = t7;
    $[109] = t60;
  } else {
    t60 = $[109];
  }
  return t60;
};